import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
  TabContent,
  TabPane,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  dismissValorcarMovement,
  fillValorcarMovement,
  getValorcarCategories,
  getValorcarSettings,
  listValorcarMovements,
  toggleValorcarSettings,
} from "../../services/valorcarServices";

const SOURCE_LABELS = {
  oos: "Sem Stock",
  delete: "Eliminar peça",
  qty_delete: "Apagar unidade",
  adjustment: "Ajuste",
};

const STATUS_LABELS = {
  pending: "Pendente",
  filled: "Preenchido",
  dismissed: "Dispensado",
};

const STATUS_COLORS = {
  pending: "warning",
  filled: "success",
  dismissed: "secondary",
};

const ValorcarList = () => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState({ enabled: false });
  const [settingsLoading, setSettingsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("pending");
  const [categories, setCategories] = useState([]);
  const [movements, setMovements] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 50;

  const [filterCategory, setFilterCategory] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // fillModal: {movement, qtyByCat: {[catId]: qty}, notes}
  const [fillModal, setFillModal] = useState(null);
  const [qtyByCat, setQtyByCat] = useState({});
  const [fillNotes, setFillNotes] = useState("");
  const [savingFill, setSavingFill] = useState(false);

  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const r = await getValorcarSettings();
      setSettings(r.data?.data || { enabled: false });
    } catch (e) {
      // assume off
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const r = await getValorcarCategories();
      setCategories(r.data?.data || []);
    } catch (e) {
      setCategories([]);
    }
  };

  const loadMovements = async () => {
    setLoading(true);
    try {
      const filters = {
        page,
        limit,
        category_id: filterCategory || undefined,
        from: filterFrom || undefined,
        to: filterTo || undefined,
      };
      if (activeTab === "pending") {
        filters.status = "pending";
        filters.is_reversal = 0;
      } else if (activeTab === "filled") {
        filters.status = "filled";
        filters.is_reversal = 0;
      } else if (activeTab === "reversal") {
        filters.is_reversal = 1;
      } else if (activeTab === "dismissed") {
        filters.status = "dismissed";
      }
      const r = await listValorcarMovements(filters);
      const data = r.data?.data || {};
      setMovements(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Erro ao carregar movimentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadCategories();
  }, []);

  useEffect(() => {
    if (settings.enabled) loadMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, settings.enabled]);

  const handleToggle = async () => {
    try {
      const r = await toggleValorcarSettings(!settings.enabled);
      setSettings(r.data?.data || { enabled: !settings.enabled });
      toast.success(
        !settings.enabled ? "Valorcar Expedições activado." : "Valorcar Expedições desactivado."
      );
    } catch (e) {
      toast.error("Erro ao alterar definição");
    }
  };

  const openFillModal = (movement) => {
    // Pré-preenche qtyByCat:
    //  1. Se já há linhas próprias gravadas → usa-as (edição).
    //  2. Caso contrário, em reversões usa as linhas do movimento
    //     ORIGINAL como ponto de partida (user só ajusta o que mudou).
    //  3. Em pendentes não-reversais sem linhas → vazio.
    const initial = {};
    const ownLines = movement.lines || [];
    if (ownLines.length > 0) {
      for (const l of ownLines) {
        initial[l.valorcar_category_id] = l.qty;
      }
    } else if (movement.is_reversal === 1) {
      for (const l of movement.original_lines || []) {
        initial[l.valorcar_category_id] = l.qty;
      }
    }
    setQtyByCat(initial);
    setFillNotes(movement.notes || "");
    setFillModal(movement);
  };

  const closeFillModal = () => {
    setFillModal(null);
    setQtyByCat({});
    setFillNotes("");
  };

  const updateCatQty = (catId, value) => {
    const n = parseInt(value, 10);
    setQtyByCat((prev) => {
      const next = { ...prev };
      if (!n || n <= 0) {
        delete next[catId];
      } else {
        next[catId] = n;
      }
      return next;
    });
  };

  const incrementCatQty = (catId, delta) => {
    setQtyByCat((prev) => {
      const cur = parseInt(prev[catId] || 0, 10);
      const next = { ...prev };
      const newVal = Math.max(0, cur + delta);
      if (newVal === 0) {
        delete next[catId];
      } else {
        next[catId] = newVal;
      }
      return next;
    });
  };

  const fillSum = useMemo(
    () =>
      Object.values(qtyByCat).reduce(
        (acc, v) => acc + (parseInt(v, 10) || 0),
        0
      ),
    [qtyByCat]
  );

  const submitFill = async () => {
    if (!fillModal) return;
    const lines = Object.entries(qtyByCat)
      .map(([catId, qty]) => ({
        valorcar_category_id: parseInt(catId, 10),
        qty: parseInt(qty, 10),
      }))
      .filter((l) => l.valorcar_category_id && l.qty > 0);

    setSavingFill(true);
    try {
      await fillValorcarMovement(fillModal.id, lines, fillNotes);
      toast.success("Movimento gravado.");
      closeFillModal();
      loadMovements();
    } catch (e) {
      toast.error("Erro ao gravar.");
    } finally {
      setSavingFill(false);
    }
  };

  const handleDismiss = async (id) => {
    if (!window.confirm("Dispensar este movimento? Não conta para o relatório.")) return;
    try {
      await dismissValorcarMovement(id);
      toast.success("Movimento dispensado.");
      loadMovements();
    } catch (e) {
      toast.error("Erro ao dispensar.");
    }
  };

  if (settingsLoading) {
    return (
      <div className="content-wrapper">
        <div className="content-header"><Spinner /></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <Row className="align-items-center">
            <Col><h1>{t("valorcar", { defaultValue: "Valorcar Expedições" })}</h1></Col>
            <Col className="text-end">
              <Link to="/valorcar/relatorios">
                <Button color="info">
                  <i className="bi bi-graph-up me-1"></i>
                  {t("valorcar_reports", { defaultValue: "Relatórios" })}
                </Button>
              </Link>
            </Col>
          </Row>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <Card className="mb-3">
            <CardBody className="d-flex align-items-center justify-content-between">
              <div>
                <strong>Módulo Valorcar Expedições</strong>
                <div className="text-muted small">
                  {settings.enabled
                    ? "Activo — todas as baixas geram movimentos pendentes para associar."
                    : "Desactivado — nenhum movimento é registado. Activa para começar."}
                </div>
              </div>
              <Button
                color={settings.enabled ? "danger" : "success"}
                onClick={handleToggle}
              >
                {settings.enabled ? "Desactivar" : "Activar"}
              </Button>
            </CardBody>
          </Card>

          {settings.enabled && (
            <Alert color="info" className="d-flex align-items-start" style={{ gap: 10 }}>
              <i className="bi bi-info-circle-fill" style={{ fontSize: 18, marginTop: 2 }}></i>
              <div>
                <strong>Movimentos são imutáveis depois de preenchidos.</strong>{" "}
                Se ajustares um movimento já preenchido, as alterações ficam registadas como{" "}
                <em>ajustes no mês actual</em> — o relatório do mês original (já comunicado à Valorcar) não muda.
              </div>
            </Alert>
          )}

          {!settings.enabled ? (
            <Alert color="warning">
              O módulo Valorcar Expedições está desactivado. Activa em cima para começar a registar movimentos.
            </Alert>
          ) : (
            <Card>
              <CardBody>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={activeTab === "pending" ? "active" : ""}
                      onClick={() => { setPage(1); setActiveTab("pending"); }}
                      style={{ cursor: "pointer" }}
                    >
                      Pendentes
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "reversal" ? "active" : ""}
                      onClick={() => { setPage(1); setActiveTab("reversal"); }}
                      style={{ cursor: "pointer" }}
                    >
                      Acertos / Reversões
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "filled" ? "active" : ""}
                      onClick={() => { setPage(1); setActiveTab("filled"); }}
                      style={{ cursor: "pointer" }}
                    >
                      Preenchidos
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "dismissed" ? "active" : ""}
                      onClick={() => { setPage(1); setActiveTab("dismissed"); }}
                      style={{ cursor: "pointer" }}
                    >
                      Dispensados
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId={activeTab}>
                    <Row className="mt-3 mb-3">
                      <Col md={4}>
                        <FormGroup>
                          <Label>Categoria</Label>
                          <Input type="select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                            <option value="">Todas</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>{c.description_pt}</option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Desde</Label>
                          <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Até</Label>
                          <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                        </FormGroup>
                      </Col>
                      <Col md={2} className="d-flex align-items-end">
                        <Button color="primary" onClick={() => { setPage(1); loadMovements(); }} className="w-100 mb-3">
                          Filtrar
                        </Button>
                      </Col>
                    </Row>

                    {loading ? (
                      <div className="text-center my-4"><Spinner /></div>
                    ) : movements.length === 0 ? (
                      <Alert color="info">Sem movimentos.</Alert>
                    ) : (
                      <Table responsive striped hover>
                        <thead>
                          <tr>
                            <th>Data</th>
                            <th>Peça</th>
                            <th>Origem</th>
                            <th>Qty</th>
                            <th>Categorias</th>
                            <th>Estado</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {movements.map((m) => (
                            <tr key={m.id}>
                              <td>{new Date(m.created_at).toLocaleString("pt-PT")}</td>
                              <td>
                                {m.parts_internal_id ? (
                                  <span>
                                    <code>{m.parts_internal_id}</code>
                                    {m.catalog_name_pt && (
                                      <div className="text-muted small">{m.catalog_name_pt}</div>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                <Badge color={m.is_reversal ? "warning" : "secondary"}>
                                  {SOURCE_LABELS[m.source] || m.source}
                                  {m.is_reversal ? " ↩" : ""}
                                </Badge>
                              </td>
                              <td><strong>{m.qty}</strong></td>
                              <td>
                                {(m.lines || []).length === 0 ? (
                                  <span className="text-muted small">—</span>
                                ) : (
                                  (m.lines || []).map((l) => (
                                    <Badge key={l.id} color="success" className="me-1">
                                      {l.category_pt}: {l.qty}
                                    </Badge>
                                  ))
                                )}
                                {m.is_reversal === 1 && (m.original_lines || []).length > 0 && (
                                  <div className="text-muted small mt-1">
                                    Original:{" "}
                                    {(m.original_lines || []).map((l) => (
                                      <span key={l.id} className="me-2">
                                        {l.category_pt}: {l.qty}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td>
                                <Badge color={STATUS_COLORS[m.status] || "secondary"}>
                                  {STATUS_LABELS[m.status] || m.status}
                                </Badge>
                              </td>
                              <td className="text-end">
                                {m.status !== "dismissed" && (
                                  <>
                                    <Button size="sm" color="primary" onClick={() => openFillModal(m)} className="me-1">
                                      {m.status === "filled" ? "Ajustar" : "Preencher"}
                                    </Button>
                                    {m.status === "pending" && (
                                      <Button size="sm" color="outline-secondary" onClick={() => handleDismiss(m.id)}>
                                        Dispensar
                                      </Button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}

                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">{total} resultado{total === 1 ? "" : "s"}</span>
                      <div>
                        <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                          ‹ Anterior
                        </Button>{" "}
                        <Button
                          size="sm"
                          color="light"
                          disabled={page * limit >= total}
                          onClick={() => setPage(page + 1)}
                        >
                          Seguinte ›
                        </Button>
                      </div>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          )}
        </div>
      </section>

      {/* Fill modal: grid de 24 categorias com inputs +/-. */}
      <Modal isOpen={!!fillModal} toggle={closeFillModal} size="xl" scrollable>
        {fillModal && (
          <>
            <ModalHeader toggle={closeFillModal}>
              {fillModal.status === "filled"
                ? "Ajustar movimento (cria ajuste no mês actual)"
                : fillModal.is_reversal === 1
                ? "Reversão — indicar unidades a reverter por categoria"
                : "Associar unidades por categoria Valorcar"}
            </ModalHeader>
            <ModalBody style={{ maxHeight: "70vh" }}>
              {fillModal.status === "filled" && (
                <Alert color="warning" className="mb-3 small">
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>
                  Este movimento já foi preenchido. Ao gravar, as diferenças (deltas) entram como <strong>ajustes no mês actual</strong>; o relatório do mês original mantém-se inalterado.
                </Alert>
              )}
              {fillModal.is_reversal === 1 && (fillModal.original_lines || []).length > 0 && (
                <Alert color="info" className="mb-3 small">
                  <strong>Movimento original:</strong>{" "}
                  {(fillModal.original_lines || []).map((l) => (
                    <span key={l.id} className="me-2">
                      {l.category_pt}: {l.qty}
                    </span>
                  ))}
                </Alert>
              )}

              <div className="text-muted small mb-2">
                Indica quantas unidades contam para cada categoria. Uma peça pode contar em várias (ex: "Frente Completa" → 2 farol nevoeiro, 2 farol dianteiro, 1 capot, 1 grelha). Deixa vazio o que não conta.
              </div>

              <Row>
                {categories.map((cat) => {
                  const v = qtyByCat[cat.id] || "";
                  const isSet = v !== "" && v !== 0;
                  return (
                    <Col xs={12} sm={6} lg={4} key={cat.id} className="mb-2">
                      <div
                        className="d-flex align-items-center justify-content-between"
                        style={{
                          padding: "6px 8px",
                          background: isSet ? "#e6f4ea" : "#f8f9fa",
                          border: isSet ? "1px solid #28a745" : "1px solid #dee2e6",
                          borderRadius: 6,
                          minHeight: 42,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0, paddingRight: 6 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 13,
                              lineHeight: 1.15,
                              wordBreak: "break-word",
                            }}
                          >
                            {cat.description_pt}
                          </div>
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: 2, flexShrink: 0 }}>
                          <Button
                            size="sm"
                            color="light"
                            onClick={() => incrementCatQty(cat.id, -1)}
                            disabled={!isSet}
                            style={{ padding: "1px 6px", fontSize: 14, fontWeight: 700 }}
                          >
                            −
                          </Button>
                          <Input
                            type="number"
                            min={0}
                            value={v}
                            onChange={(e) => updateCatQty(cat.id, e.target.value)}
                            style={{ width: 46, textAlign: "center", padding: "1px 2px", fontSize: 13 }}
                            placeholder="0"
                          />
                          <Button
                            size="sm"
                            color="light"
                            onClick={() => incrementCatQty(cat.id, +1)}
                            style={{ padding: "1px 6px", fontSize: 14, fontWeight: 700 }}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              <FormGroup className="mt-3">
                <Label>Notas (opcional)</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={fillNotes}
                  onChange={(e) => setFillNotes(e.target.value)}
                  placeholder="Ex: peça danificada, só algumas unidades contabilizadas."
                />
              </FormGroup>

              <div className="text-muted small mt-2">
                Total a contabilizar: <strong>{fillSum}</strong> unidade{fillSum === 1 ? "" : "s"} em {Object.keys(qtyByCat).length} categoria{Object.keys(qtyByCat).length === 1 ? "" : "s"}.
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={closeFillModal}>Cancelar</Button>
              <Button color="primary" onClick={submitFill} disabled={savingFill}>
                {savingFill ? <Spinner size="sm" /> : "Gravar"}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ValorcarList;
